import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { AssessmentDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import type { AssessmentRegisterResponseRow } from '@risksmart-app/drizzle/src/types';
import type { AssessmentRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  Assessment_Bool_Exp,
  GetAssessmentsQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetAssessmentsDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import { ASSESSMENT_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
  departmentsWhereOnEntity,
  tagsWhereOnEntity,
  toTrpcDashboardFilterInput,
} from '@/hooks/queries/dashboardFilterArgs';
import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

const useAssessmentsGraphqlVariables = (
  args: UseGetAssessmentsRegisterArgs
) => {
  const { user } = useRisksmartUser();
  const entityWhere = useEntityWhereFilter<Assessment_Bool_Exp>(
    Parent_Type_Enum.Assessment
  );

  const { where } = buildDashboardWhere({
    base: entityWhere,
    filters: args,
    userId: user?.userId,
    datePredicates: ASSESSMENT_DATE_PREDICATES,
    tagsWhere: tagsWhereOnEntity,
    departmentsWhere: departmentsWhereOnEntity,
    ownershipWhere: (conditions) => conditions.assessmentFilterConditions,
  });

  return { where };
};

const mapTrpcAssessmentToGraphQL = (
  assessment: AssessmentRegisterResponseRow
): StrictMappedGraphQLSelection<GetAssessmentsQuery['assessment'][number]> => {
  return {
    ...assessment,
    assessedItems: assessment.assessmentResults,
  };
};

/**
 * Maps TRPC assessment data to match the GraphQL query structure
 */
export function mapTrpcAssessmentsToGraphQL(
  trpcData: AssessmentRegisterResponse
): StrictMappedGraphQLSelection<GetAssessmentsQuery> {
  return {
    assessment: trpcData.assessment.map((assessment) =>
      mapTrpcAssessmentToGraphQL(assessment)
    ),
  };
}

export type UseGetAssessmentsRegisterArgs =
  DashboardFilterArgs<AssessmentDashboardDateField>;

export const useGetAssessmentsRegister = createQueryHook<
  UseGetAssessmentsRegisterArgs,
  AssessmentRegisterResponse,
  GetAssessmentsQuery
>({
  trpcQueryOptions: (
    trpc,
    { tagTypeIds, departmentTypeIds, dateRange, ownership }
  ) =>
    trpc.frontend.assessment.register.queryOptions(
      toTrpcDashboardFilterInput({
        tagTypeIds,
        departmentTypeIds,
        dateRange,
        ownership,
      })
    ),
  mapTrpcDataToGraphQL: mapTrpcAssessmentsToGraphQL,
  graphqlDocument: GetAssessmentsDocument,
  graphqlVariables: useAssessmentsGraphqlVariables,
});
